// src/features/developers/i18n/es.ts
//
// Spain Spanish, formal register. The code samples and the names of headers, paths and fields are
// not translated: they are the API's. Nothing here names the technology underneath.

export default {
  name: 'Desarrolladores',
  title: 'Desarrolladores',
  role: 'Desarrollador',
  intro:
    'Conecte sus sistemas con Insight.',
  tabs: {
    label: 'Documentación para desarrolladores',
    overview: 'Primeros pasos',
    reference: 'Referencia',
    recipes: 'Ejemplos',
    downloads: 'Descargas',
  },
  pills: {
    version: 'v%{version}',
    operations: '%{smart_count} operación |||| %{smart_count} operaciones',
    thisEnvironment: 'Este entorno',
  },
  environments: {
    production: 'Producción',
    development: 'Pruebas (sandbox)',
  },
  loading: 'Cargando…',
  copy: 'Copiar',
  copied: 'Copiado',
  errors: {
    forbidden: 'Su usuario no tiene acceso a la documentación para desarrolladores. Solicite acceso a su administrador o a Roommatik.',
    unavailable: 'La documentación no está disponible en este momento. Los primeros pasos y los ejemplos no dependen de ella. Inténtelo de nuevo más tarde.',
    viewer: 'No se ha podido cargar el visor de la referencia. Actualice la página e inténtelo de nuevo.',
    download: 'La descarga no se ha completado. Inténtelo de nuevo.',
  },
  pitch: {
    eyebrow: 'Conecte sus sistemas',
    title: 'Los datos de su operación, a un paso de donde los necesita',
    text:
      'Insight reúne lo que ocurre en la recepción de sus instalaciones. Con esta API, esos datos fluyen en las dos direcciones: sin abrir la aplicación y sin proyectos a medida.',
    send: {
      title: 'Envíe datos a Insight',
      text:
        'Su sistema de reservas, sus cerraduras, su aplicación para huéspedes… Lo que ocurre en ellos aparece en los indicadores de Insight, junto a lo que ya registran los kioscos, sin pantallas nuevas que aprender.',
      points: [
        'Reservas, estancias, recorridos de huésped y estado de dispositivos.',
        'Lotes de hasta 500 eventos en un formato abierto.',
        'Reenviar un evento es seguro: se reconoce como duplicado.',
      ],
    },
    read: {
      title: 'Lleve los datos de Insight a sus sistemas',
      text:
        'Los indicadores, los check-ins y las reservas de cada instalación, listos para su herramienta de análisis, su intranet o su sistema de revenue.',
      points: [
        'Los mismos indicadores que usted ve en Insight.',
        'Check-ins y reservas por instalación, con el huésped como «Juan G.».',
        'Solo las instalaciones que usted autorice.',
      ],
    },
  },
  useCases: {
    title: 'Qué puede construir',
    bi: {
      title: 'Su propio cuadro de mando',
      text: 'Lleve los indicadores de todas sus instalaciones a su herramienta de análisis y combínelos con sus ventas y su ocupación.',
    },
    intranet: {
      title: 'Una intranet de operaciones',
      text: 'Muestre a su equipo las llegadas y los check-ins del día de cada instalación, sin que nadie tenga que abrir otra aplicación.',
    },
    revenue: {
      title: 'Revenue y planificación',
      text: 'Cruce las reservas con llegada en cada periodo y el uso del autoservicio con sus previsiones y sus tarifas.',
    },
    systems: {
      title: 'Su sistema, en los indicadores',
      text: 'Haga que su sistema de reservas o de cerraduras informe a Insight, y vea sus eventos en los mismos indicadores que los del kiosco.',
    },
  },
  cta: {
    title: '¿Quiere conectar sus sistemas con Insight?',
    text: 'Roommatik le entrega las claves de acceso después de confirmar con usted las instalaciones y los datos que va a enviar o leer.',
    button: 'Solicitar acceso',
    subject: 'Solicitud de acceso a la API de Insight',
    body:
      'Buenos días:\n\nQuisiéramos conectar nuestros sistemas con Insight.\n\nOrganización:\nInstalaciones:\nQué queremos hacer (enviar datos, leer datos o ambas cosas):\n\nGracias.',
  },
  quickStart: {
    title: 'Primeros pasos',
    steps: {
      access: {
        title: 'Solicite sus claves',
        text:
          'Roommatik emite las claves: una para leer datos y otra para cada sistema que envía datos. Se muestran una sola vez, así que guárdelas en su servidor.',
      },
      check: {
        title: 'Compruebe su clave',
        text: 'Esta llamada devuelve las instalaciones que su clave puede leer. Si la respuesta las lista, ya está conectado.',
      },
      read: {
        title: 'Lea los indicadores',
        text: 'Pida las cifras de un periodo, los check-ins o las reservas de una instalación.',
      },
      send: {
        title: 'Envíe sus eventos',
        text: 'Mande lotes de eventos desde su sistema. Pruebe primero en el entorno de pruebas.',
      },
    },
    toRecipes: 'Ver los ejemplos',
    toReference: 'Abrir la referencia',
  },
  cards: {
    environments: {
      title: 'Entornos',
      text:
        'Los datos y las claves son independientes en cada entorno: una clave de uno no funciona en el otro. El entorno de pruebas tiene instalaciones de prueba: verifique allí su integración antes de pasar a producción.',
    },
    authentication: {
      title: 'Autenticación',
      head: ['Cabecera', 'Para qué sirve'],
      rows: [
        ['`X-Api-Key`', 'Leer datos. La clave pertenece a su organización y está limitada a las instalaciones autorizadas. Empieza por `ink_`.'],
        ['`X-Source-Key`', 'Enviar datos. La clave pertenece a un remitente y a las instalaciones de las que puede informar. Empieza por `rmk2_`.'],
      ],
      text:
        'Guarde las claves en su servidor, nunca en una página web ni en una aplicación móvil. Una clave revocada deja de funcionar al instante y recibe un 401.',
    },
    requests: {
      title: 'Peticiones',
      items: [
        'Todo es HTTPS y JSON.',
        'Las fechas tienen el formato `AAAA-MM-DD` y siguen el calendario de la instalación.',
        'Un periodo admite un máximo de 92 días.',
        'Los listados se paginan con `page` y `pageSize` (hasta 200; 50 por defecto).',
      ],
    },
    responses: {
      title: 'Respuestas',
      text:
        'Las lecturas devuelven `success: true` y el resultado en `data`; los listados añaden `paging`. Los errores tienen la misma forma, con un código y un mensaje. Fíese del código HTTP.',
    },
    errors: {
      title: 'Errores',
      head: ['Código', 'Qué significa'],
      rows: [
        ['`400`', 'Falta un parámetro o no es válido; el mensaje indica cuál.'],
        ['`401`', 'La clave falta, no se reconoce o ha sido revocada.'],
        ['`404`', 'La instalación no existe o no está disponible para su clave.'],
        ['`429`', 'Demasiadas peticiones: espere los segundos que indica la cabecera `Retry-After`.'],
      ],
    },
    limits: {
      title: 'Límites',
      items: [
        '60 peticiones por minuto y clave; al superarlo se responde 429 con `Retry-After`.',
        'Periodos de hasta 92 días y hasta 10 indicadores por petición.',
        '`pageSize` de hasta 200.',
        'Lotes de hasta 500 eventos y 2 MB por petición.',
      ],
    },
    personalData: {
      title: 'Datos personales',
      text:
        'Los check-ins y las reservas identifican al huésped solo por su nombre y la inicial del apellido («Juan G.»). Nunca se devuelven documentos, correos electrónicos, teléfonos, fechas de nacimiento ni direcciones.',
    },
    assistant: {
      title: 'Trabaje con un asistente de programación',
      text:
        'Descargue el archivo OpenAPI y entrégueselo a su asistente de programación o a un generador de clientes: es el contrato exacto de la versión desplegada, con un ejemplo en cada petición.',
      button: 'Ir a las descargas',
    },
  },
  reference: {
    loading: 'Cargando la referencia…',
  },
  recipes: {
    intro:
      'Los ejemplos apuntan a `%{baseUrl}`, el entorno que está viendo, y leen las claves del entorno: defina `INSIGHT_API_KEY` para leer datos e `INSIGHT_SOURCE_KEY` para enviarlos antes de ejecutarlos.',
    language: 'Lenguaje',
    properties: {
      title: 'Descubra qué instalaciones puede leer',
      description:
        'Devuelve las instalaciones de su clave con su identificador, su zona horaria y su país. Use ese identificador en el resto de las llamadas.',
    },
    indicators: {
      title: 'Lea los indicadores de un periodo',
      description:
        'Pida hasta 10 indicadores de una instalación para un periodo de hasta 92 días. Cada uno trae su valor, el tamaño de la muestra y, si no se puede calcular, el motivo. El catálogo completo está en `GET /api/v2/data/metrics`. Sustituya el identificador por uno de la primera llamada.',
    },
    checkins: {
      title: 'Recorra los check-ins de una instalación',
      description:
        'Los listados se paginan: pida páginas hasta reunir `paging.total` elementos. Las reservas con llegada en el periodo se leen igual en `/reservations`. El huésped se muestra como «Juan G.».',
    },
    events: {
      title: 'Envíe un lote de eventos',
      description:
        'Un lote es una lista de hasta 500 eventos, y usa la clave de remitente. La respuesta indica, evento a evento, si se almacenó, si era un duplicado o si se rechazó. Pruebe primero en el entorno de pruebas.',
    },
  },
  downloads: {
    postman: {
      title: 'Postman',
      text:
        'Cada operación de la referencia como una petición que se ejecuta tal como se descarga, con los cuerpos rellenos y las claves en variables.',
      collection: 'Descargar la colección',
      steps: [
        'Importe la colección y un archivo de entorno en Postman.',
        'Seleccione el entorno y defina `apiKey` y `sourceKey`.',
        'Abra `Read data` y su primera petición, y pulse Enviar.',
      ],
    },
    openapi: {
      title: 'OpenAPI',
      text:
        'La especificación OpenAPI 3 de la versión desplegada ahora mismo (v%{version}). Genere un cliente tipado, impórtela en Insomnia, Bruno o Hoppscotch, o entregue a un asistente de programación el contrato exacto.',
      button: 'Descargar el archivo OpenAPI',
      note:
        'Se valida contra la implementación en cada compilación: una ruta que existe sin estar documentada, o documentada sin existir, rompe la compilación. No se desvía de lo que está desplegado.',
    },
  },
  keys: {
    open: 'Claves de acceso',
    back: 'Volver a Desarrolladores',
    title: 'Claves de acceso',
    subtitle: 'Las claves que permiten a un cliente leer los datos de sus instalaciones. Solo las emite y las revoca el personal de Roommatik.',
    empty: 'Todavía no se ha emitido ninguna clave.',
    create: 'Crear clave',
    columns: {
      name: 'Nombre',
      owner: 'Titular',
      properties: 'Instalaciones',
      key: 'Clave',
      createdBy: 'Creada por',
      created: 'Creada',
      lastUsed: 'Último uso',
      status: 'Estado',
    },
    status: { active: 'Activa', revoked: 'Revocada' },
    neverUsed: 'Sin uso',
    propertiesCount: '%{smart_count} instalación |||| %{smart_count} instalaciones',
    revoke: 'Revocar',
    form: {
      title: 'Nueva clave de acceso',
      name: 'Nombre de la clave',
      nameHelp: 'Por ejemplo, «Integración con el ERP».',
      owner: 'Organización titular',
      ownerHelp: 'La organización que usará la clave.',
      properties: 'Instalaciones',
      propertiesHelp: 'La clave solo podrá leer estas instalaciones.',
      selectAll: 'Seleccionar todas',
      noProperties: 'No hay instalaciones disponibles.',
      cancel: 'Cancelar',
      submit: 'Crear clave',
      invalid: {
        name: 'Introduzca un nombre.',
        nameTooLong: 'El nombre admite hasta 120 caracteres.',
        owner: 'Introduzca la organización titular.',
        ownerTooLong: 'La organización admite hasta 200 caracteres.',
        properties: 'Seleccione al menos una instalación.',
      },
    },
    failures: {
      INVALID_NAME: 'El nombre no es válido.',
      INVALID_OWNER: 'La organización titular no es válida.',
      INVALID_PROPERTIES: 'Las instalaciones seleccionadas no son válidas.',
      UNKNOWN_PROPERTIES: 'Alguna de las instalaciones no está disponible en este entorno.',
      UNKNOWN_ORGANIZATION: 'La organización indicada no existe.',
      generic: 'No se ha podido crear la clave. Inténtelo de nuevo.',
    },
    created: {
      title: 'Clave creada',
      warning: 'Guárdela ahora: no se volverá a mostrar.',
      text: 'Entregue esta clave a su titular por un canal seguro. Roommatik no podrá mostrársela de nuevo.',
      copy: 'Copiar clave',
      copied: 'Copiada',
      close: 'Cerrar',
    },
    revokeDialog: {
      title: 'Revocar la clave «%{name}»',
      text: 'La clave dejará de funcionar al instante y la integración que la use recibirá errores 401. Esta acción no se puede deshacer.',
      confirm: 'Revocar clave',
      cancel: 'Cancelar',
      failed: 'No se ha podido revocar la clave. Inténtelo de nuevo.',
    },
  },
};
